import { useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingCart, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function Cart() {
  const { items, updateQty, removeItem, total } = useCart();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map((i) => ({ lookup_key: i.lookup_key, quantity: i.quantity })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setLoading(false);
    }
  };

  if (items.length === 0)
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <span className="inline-grid place-items-center w-20 h-20 rounded-3xl bg-slate-100 text-slate-400 mb-6">
          <ShoppingCart className="w-10 h-10" />
        </span>
        <h1 className="font-heading font-extrabold text-3xl text-slate-900">Votre panier est vide</h1>
        <p className="text-slate-500 mt-2">Découvrez nos kits et goodies dans la boutique.</p>
        <Link to="/boutique" className="inline-flex items-center gap-2 mt-8 px-7 py-3.5 rounded-full bg-sky-500 text-white font-bold hover:bg-sky-600 transition-colors">
          Aller à la boutique <ArrowRight className="w-5 h-5" />
        </Link>
      </div>
    );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="font-heading font-black text-4xl text-slate-900 mb-8">Mon panier</h1>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
          {items.map((i) => (
            <div key={i.lookup_key} className="flex items-center gap-4 bg-white rounded-3xl border border-slate-100 shadow-sm p-4" data-testid={`cart-item-${i.id}`}>
              <img src={i.image} alt={i.name} className="w-20 h-20 rounded-2xl object-cover" />
              <div className="flex-1 min-w-0">
                <p className="font-heading font-extrabold text-slate-900 truncate">{i.name}</p>
                <p className="text-sm text-slate-500">{i.price.toFixed(2)} €</p>
              </div>
              <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1">
                <button onClick={() => updateQty(i.lookup_key, i.quantity - 1)} className="grid place-items-center w-8 h-8 rounded-full bg-white text-slate-700 hover:bg-slate-200" data-testid={`cart-dec-${i.id}`}>
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-8 text-center font-bold" data-testid={`cart-qty-${i.id}`}>{i.quantity}</span>
                <button onClick={() => updateQty(i.lookup_key, i.quantity + 1)} className="grid place-items-center w-8 h-8 rounded-full bg-white text-slate-700 hover:bg-slate-200" data-testid={`cart-inc-${i.id}`}>
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <p className="font-heading font-extrabold text-slate-900 w-20 text-right hidden sm:block">
                {(i.price * i.quantity).toFixed(2)} €
              </p>
              <button onClick={() => removeItem(i.lookup_key)} className="grid place-items-center w-9 h-9 rounded-full text-rose-400 hover:bg-rose-50" data-testid={`cart-remove-${i.id}`}>
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 h-fit">
          <h2 className="font-heading font-extrabold text-xl text-slate-900 mb-4">Récapitulatif</h2>
          <div className="flex items-center justify-between text-slate-600 mb-2">
            <span>Sous-total</span>
            <span className="font-bold" data-testid="cart-subtotal">{total.toFixed(2)} €</span>
          </div>
          <p className="text-xs text-slate-400 mb-4">Taxes calculées à l'étape de paiement.</p>
          <div className="border-t border-slate-100 pt-4 flex items-center justify-between">
            <span className="font-heading font-extrabold text-slate-900">Total</span>
            <span className="font-heading font-black text-2xl text-slate-900" data-testid="cart-total">{total.toFixed(2)} €</span>
          </div>
          <button
            onClick={checkout}
            disabled={loading}
            data-testid="checkout-button"
            className="w-full mt-6 py-3.5 rounded-full bg-sky-500 text-white font-bold hover:bg-sky-600 hover:scale-[1.02] transition-[transform,background-color] disabled:opacity-60"
          >
            {loading ? "Redirection..." : "Passer au paiement"}
          </button>
        </div>
      </div>
    </div>
  );
}
